import { Inject, Injectable, LOCALE_ID, signal } from '@angular/core';
import { VotedEventRecord, Ballot, ElectorAndBallot } from '../ballot/backend-types';
import { BallotService } from '../ballot/ballot.service';
import { ApiAboutService } from '../Services/api-about.service';
import { SessionStorageService } from '../Services/session-storage.service';

@Injectable({
  providedIn: 'root'
})
export class BallotListService {

  private _ballots: Ballot[]
  private ballotURL: string | undefined;
  private envelopeURL: string | undefined;
  returnOptions: string[] = [];
  // Saving events here too in order to transition to the dashboard model.
  dashboardEvents = signal<Ballot[]>([]);
  votedEventInfo = signal<VotedEventRecord[]>([]);

  constructor(
    private readonly ballotService: BallotService,
    private storer: SessionStorageService,
    private readonly aboutService: ApiAboutService,
    @Inject(LOCALE_ID) readonly locale: string
  ) {
    this._ballots = storer.getBallotList() ?? [];
  }

  getBallots(): Ballot[] {
    if(this.aboutService.VWAFlowType === 'GLOBAL'){
      return this._ballots;
    } else {
      return this._ballots.filter((b) => !b.voted);
    }
  }

  getBallotURL(): string | undefined {
    return this.ballotURL;
  }

  getEnvelopeURL(): string | undefined {
    return this.envelopeURL;
  }

  setBallots(elector: ElectorAndBallot | null) {
    this._ballots = elector?.events ?? [];
    this.votedEventInfo.set(elector?.votedEventInfo ?? []);
    this.dashboardEvents.set(elector?.events ?? []);
    this.storer.setBallotList(this._ballots)
    if (this._ballots.length > 0) {
      this.setBallotService(0)
    }
    this.ballotURL = elector?.ballotURL;
    this.envelopeURL = elector?.envelopeURL;
  }

  setBallotService(index: number) {
    this.ballotService.setBallot(this.getBallots()[index])
  }

  getOverallIndexForEventID(eventID: number){
    return this.getBallots().findIndex((b) => b.eventId === eventID);
  }

  voted(ballot?: Ballot) {
    if (ballot) {
      const event = this._ballots.find(b => b.eventId === ballot.eventId)
      if (event) {
        event.voted = true
        let votedEventIndex = this.votedEventInfo().findIndex((b) => b.eventId === ballot.eventId);
        if(votedEventIndex > -1){
          this.votedEventInfo()[votedEventIndex].lastUseTs = new Date().toISOString();
        } else {
          this.votedEventInfo().push({
            eventId: event.eventId,
            lastUseTs: new Date().toISOString()
          })
        }
      }
      this.storer.setBallotList(this._ballots);
      this.dashboardEvents.set(this._ballots);
    }
  }

  formatDateRange(ballot: Ballot): string {
    let formattedStart = new Date(ballot.eventActivation).toLocaleString(this.locale, {
       month: 'short',
         day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
      minute: 'numeric'
    });
    let formattedEnd = new Date(ballot.eventExpiration).toLocaleString(this.locale, {
       month: 'short',
         day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
      minute: 'numeric'
    });
    return $localize`${formattedStart} to ${formattedEnd}`;
  }

  unvotedOrRecurrence(eventID: number): boolean {
    let event: Ballot | undefined = this.dashboardEvents().find((item) => item.eventId === eventID);
    if(!event) {
      return false;
    } else if(event.recurrenceInterval){
      return true;
    } else {
      let votedTime = this.votedEventInfo().find((item) => item.eventId === eventID)?.lastUseTs;
      if(votedTime) {
        return false;
      } else {
        return true;
      }
    }
  }

  isEventOpen(expiration: string | Date): boolean {
    const expirationDate = new Date(expiration);
    return expirationDate.getTime() > new Date().getTime();
  }

  alreadyVoted(eventId: number, recurrence?: string): boolean {
    const votedEvent = this.votedEventInfo().find(evento => evento.eventId === eventId);
    if (!votedEvent) return false;
    if (!recurrence) return true;
    if (!votedEvent.lastUseTs) return true;
    const lastTimeVoted = new Date(votedEvent.lastUseTs);
    const now = new Date();
    const diffMs = now.getTime() - lastTimeVoted.getTime();

    switch (recurrence.toLowerCase()) {
      case 'second':
        return diffMs < 1000; 
      case 'minute':
        return diffMs < 60 * 1000; 
      case 'hour':
        return diffMs < 60 * 60 * 1000; 
      case 'day':
        return diffMs < 24 * 60 * 60 * 1000; 
      default:
        return true;
    }
  }

  isActionable(ballot: Ballot | undefined) {
    if(ballot) {
      return this.isEventOpen(ballot.eventExpiration) && !this.alreadyVoted(ballot.eventId, ballot.recurrenceInterval ?? "");
    } else {
      return false;
    }
  }

  canViewPollResults(poll: Ballot): boolean {
    let votedEvent: VotedEventRecord | undefined = this.votedEventInfo().find(event => event.eventId === poll.eventId);
    return (poll.voteEventData.showResults ?? false) && !!votedEvent;
  }

  eventStatus(ballot: Ballot): EventStatus {
    if(this.isEventOpen(ballot.eventExpiration)) {
      if(this.alreadyVoted(ballot.eventId)) {
        if(this.alreadyVoted(ballot.eventId, ballot.recurrenceInterval)) {
          return 'SUBMITTED'
        } else {
          return 'CAN_CHANGE';
        }
      } else {
        let fractionRemaining = this.timeRemaining(ballot)/this.totalTime(ballot);
        if(fractionRemaining < 0.02) {
          return 'CLOSES_VERY_SOON';
        } else if (fractionRemaining < 0.1 ) {
          return 'CLOSES_SOON';
        } else {
          return 'VOTE';
        }
      }
    } else {
      if(this.alreadyVoted(ballot.eventId)) {
        return 'SUBMITTED';
      } else {
        return 'EXPIRED';
      }
    }
  }

  // The total amount of time (in seconds) for the event.
  totalTime(ballot: Ballot): number {
    let start: number = new Date(ballot.eventActivation).getTime();
    let close: number = new Date(ballot.eventExpiration).getTime();

    return (close - start)/1000;
  }

  // The amount of time remaining in the event (in seconds).
  timeRemaining(ballot: Ballot): number {
    let now: number = Date.now();
    let close: number = new Date(ballot.eventExpiration).getTime();
    return (close-now)/1000;
  }
}


export type EventSortOrder = 'EXPIRATION_ASCENDING' | 'EXPIRATION_DESCENDING' | 'NEWEST_FIRST' | 'OLDEST_FIRST';

export type EventStatus = 'VOTE' | 'CLOSES_SOON' | 'CLOSES_VERY_SOON' | 'SUBMITTED' | 'CAN_CHANGE' | 'EXPIRED';